import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { Tooltip } from 'antd';
import {
  getAssetsPositionLabel,
  getAssetsSliderSkeletonKey,
  isApprovalAsset,
  useAssetsSliderEdgeLoading,
  useAssetsSliderReview,
  useScrollToActiveAsset
} from './hooks';
import { useAssetsSliderNavigation } from './AssetsSliderProvider';
import { useResponsive } from '@hooks/useResponsive';
import { AssetItemDto, AWAssetDto } from '@api/Api';
import { useTypedSelector } from '@hooks';
import { useMediaViewerMobileActionVisibility } from '@pages/MediaViewer/components/MediaViewerMobileAction/MediaViewerMobileAction';
import { assetPreviewThumbnails } from '@helpers/assetPreviewThumbnails';
import useTypedDispatch from '@hooks/useTypedDispatch';
import { setAssetsSliderOpen } from '@redux/actions/mediaViewerAction';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import {
  ArrowDownIcon,
  CheckTaskIcon,
  GalleryHorizontalIcon,
  LockIcon
} from '@icons';

import './AssetsSlider.less';

type AssetsSliderItemProps = {
  asset: AWAssetDto | AssetItemDto;
  isActive: boolean;
  onClick: () => void;
};

function AssetsSliderItem({ asset, isActive, onClick }: AssetsSliderItemProps) {
  const decision = isApprovalAsset(asset) ? asset.decision : null;
  const accessDisabled = isApprovalAsset(asset) ? asset.accessDisabled : false;

  const disableReason = accessDisabled ? (
    <>
      Your access to this media is restricted.
      <br />
      Contact the approval manager
      <br />
      if you need to request access.
    </>
  ) : null;

  const showLock = !!disableReason;
  const grayOut = !!disableReason || !!decision;

  const previewUrl = isApprovalAsset(asset)
    ? asset.previewUrl
    : asset.asset?.versions[0]?.metadata.preview?.url;
  const assetType = isApprovalAsset(asset)
    ? asset.type
    : asset.asset?.versions[0]?.metadata.type;

  const preparePreview = useMemo(() => {
    const res = assetPreviewThumbnails({
      url: previewUrl,
      status: 'ready',
      type: assetType || 'unknown',
      format: 'img'
    });

    if (typeof res === 'string')
      return <img draggable={false} src={res || ''} alt="" />;
    return res;
  }, [previewUrl, assetType]);

  const name = isApprovalAsset(asset)
    ? asset.name
    : asset.asset?.versions[0]?.name;
  const extension = isApprovalAsset(asset)
    ? asset.extension
    : asset.asset?.versions[0]?.extension;
  const versionNumber = isApprovalAsset(asset)
    ? asset.versionsCount
    : asset.asset?.versions[0]?.versionNumber;

  return (
    <Tooltip
      overlayClassName="assets-slider_tooltip"
      title={
        <>
          {!disableReason && (
            <div className="assets-slider_tooltip-head">
              <div className="assets-slider_tooltip-name">
                {name}
                <span className="assets-slider_tooltip-extension">
                  .{extension}
                </span>
              </div>
              <div className="assets-slider_tooltip-version">
                V{versionNumber}
              </div>
            </div>
          )}
          {disableReason && (
            <div className="assets-slider_tooltip-body">{disableReason}</div>
          )}
        </>
      }
    >
      <div
        className={classNames('assets-slider_item', {
          'assets-slider_item--active': isActive,
          'assets-slider_item--disabled': grayOut,
          'assets-slider_item--blur': accessDisabled
        })}
        onClick={accessDisabled ? undefined : onClick}
        style={{ cursor: accessDisabled ? 'not-allowed' : undefined }}
        data-slider-key={asset.id}
      >
        {showLock && (
          <div className="assets-slider_item-lock-icon">
            <LockIcon />
          </div>
        )}
        {preparePreview}
        {!!decision && (
          <div
            className={classNames([
              'assets-slider_item-icon',
              `b-color-${decision}`
            ])}
          >
            <CheckTaskIcon />
          </div>
        )}
      </div>
    </Tooltip>
  );
}

const noAssets: (AWAssetDto | AssetItemDto)[] = [];
const MAX_SKELETON_ITEMS = 20;

function AssetsSliderSkeleton({ count }: { count: number }) {
  return (
    <>
      {Array.from({ length: Math.min(count, MAX_SKELETON_ITEMS) }, (_, i) => (
        <div
          key={i}
          className="assets-slider_item assets-slider_item--skeleton"
        />
      ))}
    </>
  );
}

type AssetsSliderNavigation = ReturnType<typeof useAssetsSliderNavigation>;

/** Active entry: asset id, or the selected skeleton's key */
function getActiveSliderKey({
  activeAssetId,
  skeletonPosition
}: AssetsSliderNavigation) {
  return skeletonPosition !== null
    ? getAssetsSliderSkeletonKey(skeletonPosition)
    : activeAssetId;
}

/** Loaded assets, with skeletons for entries of not yet loaded pages */
function renderSliderEntries({
  sortedAssets,
  activeAssetId,
  skeletonPosition,
  loadedStartIndex,
  leadingSkeletonCount,
  trailingSkeletonCount,
  onChangeAsset
}: AssetsSliderNavigation) {
  const renderSkeleton = (position: number) => (
    <div
      key={getAssetsSliderSkeletonKey(position)}
      data-slider-key={getAssetsSliderSkeletonKey(position)}
      className={classNames('assets-slider_item assets-slider_item--skeleton', {
        'assets-slider_item--active': position === skeletonPosition
      })}
    />
  );
  const loadedEndIndex = loadedStartIndex + sortedAssets.length;
  return [
    ...Array.from({ length: leadingSkeletonCount }, (_, i) =>
      renderSkeleton(loadedStartIndex - leadingSkeletonCount + i)
    ),
    ...sortedAssets.map((item) => {
      const isActive = activeAssetId === item.id;
      return (
        <AssetsSliderItem
          asset={item}
          isActive={isActive}
          onClick={() => {
            if (isActive) return;
            onChangeAsset(item);
          }}
          key={item.id}
        />
      );
    }),
    ...Array.from({ length: trailingSkeletonCount }, (_, i) =>
      renderSkeleton(loadedEndIndex + i)
    )
  ];
}

function AssetsSliderMobile() {
  const { tourData, goToStep } = useOnboardingProcess();
  const selectedAsset = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.assets
  );
  const selectedVersion = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.selectedVersion
  );
  const navigation = useAssetsSliderNavigation();
  const {
    aw,
    isApprovalList,
    sortedAssets,
    count,
    currentAssetPosition,
    isSelectedAssetPending,
    loadMore,
    scrollLeftRef,
    isMobileExpanded,
    setIsMobileExpanded
  } = navigation;
  // Hooks work with rendered items only, which are skeletons while pending
  const displayedAssets = isSelectedAssetPending ? noAssets : sortedAssets;
  const { reviewPositionLabel, isReviewed } = useAssetsSliderReview(aw);
  const canShowFloatComments = !!selectedAsset && !!selectedVersion;
  const mobileVisibility = useMediaViewerMobileActionVisibility({
    isShowFloatCommentForm: canShowFloatComments,
    isShowFloatCommentItem: canShowFloatComments
  });
  const isAnnouncementMediaViewerMobileTour =
    tourData?.type === 'announcement-media-viewer-mobile';
  const isAnnouncementMediaViewerCarouselTour =
    isAnnouncementMediaViewerMobileTour && goToStep === 0;
  const contentRef = useRef<HTMLDivElement>(null);
  const hideMobileReviewBanner = !!mobileVisibility?.showFloatCommentItem;

  useEffect(() => {
    if (hideMobileReviewBanner) {
      setIsMobileExpanded(false);
    }
  }, [hideMobileReviewBanner, setIsMobileExpanded]);

  useScrollToActiveAsset({
    contentRef,
    scrollLeftRef,
    sortedAssets: displayedAssets,
    activeKey: getActiveSliderKey(navigation),
    enabled: isMobileExpanded
  });

  useAssetsSliderEdgeLoading({
    contentRef,
    sortedAssets: displayedAssets,
    loadMore,
    enabled: isMobileExpanded
  });

  if (count <= 1 && !isAnnouncementMediaViewerMobileTour) {
    return null;
  }

  if (hideMobileReviewBanner) {
    return null;
  }

  const sliderItems = renderSliderEntries(navigation);

  return (
    <div
      className={classNames('assets-slider', {
        'assets-slider--mobile-expanded': isMobileExpanded,
        'assets-slider--mobile-collapsed': !isMobileExpanded
      })}
    >
      <div
        className={classNames('assets-slider_mobile-expandable', {
          'assets-slider_mobile-expandable--expanded': isMobileExpanded
        })}
      >
        <div className="assets-slider_mobile-expandable_section">
          <div
            className={classNames(
              'assets-slider_mobile',
              'tour-announcement-media-viewer-carousel',
              {
                'tour-announcement-media-viewer-carousel-active':
                  isAnnouncementMediaViewerCarouselTour
              }
            )}
            onClick={() => setIsMobileExpanded(true)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsMobileExpanded(true);
              }
            }}
          >
            <div
              className={classNames('assets-slider_mobile-icon', {
                'assets-slider_mobile-icon--reviewed': isReviewed
              })}
            >
              {isApprovalList ? <CheckTaskIcon /> : <GalleryHorizontalIcon />}
            </div>
            <div className="assets-slider_mobile-label">
              {isApprovalList
                ? `Review${
                    reviewPositionLabel ? `: ${reviewPositionLabel}` : ''
                  }`
                : getAssetsPositionLabel(currentAssetPosition, count)}
            </div>
            <ArrowDownIcon className="assets-slider_mobile-chevron" />
          </div>
        </div>
        <div className="assets-slider_mobile-expandable_section">
          <div className="assets-slider_mobile-panel">
            <div ref={contentRef} className="assets-slider_content">
              {isSelectedAssetPending ? (
                <AssetsSliderSkeleton count={count} />
              ) : (
                sliderItems
              )}
            </div>
            <div
              className="assets-slider_toggle"
              role="button"
              tabIndex={0}
              onClick={() => setIsMobileExpanded(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setIsMobileExpanded(false);
                }
              }}
            >
              <ArrowDownIcon className="assets-slider_toggle-chevron" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AssetsSliderDesktop() {
  const dispatch = useTypedDispatch();
  const isAssetsSliderOpen = useTypedSelector(
    ({ mediaViewer }) => mediaViewer.isAssetsSliderOpen
  );
  const navigation = useAssetsSliderNavigation();
  const {
    aw,
    isApprovalList,
    sortedAssets,
    count,
    isSelectedAssetPending,
    loadMore,
    scrollLeftRef
  } = navigation;
  // Hooks work with rendered items only, which are skeletons while pending
  const displayedAssets = isSelectedAssetPending ? noAssets : sortedAssets;
  const { reviewPositionLabel, isReviewed } = useAssetsSliderReview(aw);
  const contentRef = useRef<HTMLDivElement>(null);
  const contentWrapRef = useRef<HTMLDivElement>(null);

  useScrollToActiveAsset({
    contentRef,
    scrollLeftRef,
    sortedAssets: displayedAssets,
    activeKey: getActiveSliderKey(navigation),
    enabled: isAssetsSliderOpen
  });

  useAssetsSliderEdgeLoading({
    contentRef,
    sortedAssets: displayedAssets,
    loadMore,
    enabled: isAssetsSliderOpen
  });

  // Layout effect: the edge fades are set before the first paint. The slider
  // is remounted for every asset, so their transition is skipped while it
  // appears, otherwise the fade in replays on every selection
  useLayoutEffect(() => {
    if (!isAssetsSliderOpen) return;
    const content = contentRef.current;
    const wrap = contentWrapRef.current;
    if (!content || !wrap) return;

    wrap.classList.add('assets-slider_content-wrap--appearing');
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() =>
        wrap.classList.remove('assets-slider_content-wrap--appearing')
      );
    });

    const updateFades = () => {
      const maxScroll = content.scrollWidth - content.clientWidth;
      wrap.classList.toggle(
        'assets-slider_content-wrap--fade-left',
        content.scrollLeft > 1
      );
      wrap.classList.toggle(
        'assets-slider_content-wrap--fade-right',
        content.scrollLeft < maxScroll - 1
      );
    };

    updateFades();
    content.addEventListener('scroll', updateFades);
    return () => {
      cancelAnimationFrame(frame);
      content.removeEventListener('scroll', updateFades);
    };
  }, [isAssetsSliderOpen, count]);

  useEffect(() => {
    if (!isAssetsSliderOpen) return;
    const content = contentRef.current;
    if (!content) return;

    let isPointerDown = false;
    let isDragging = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      isPointerDown = true;
      isDragging = false;
      startX = e.clientX;
      startScrollLeft = content.scrollLeft;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!isPointerDown) return;
      // Released outside of the slider before dragging started
      if (!isDragging && e.buttons === 0) {
        isPointerDown = false;
        return;
      }
      const dx = e.clientX - startX;
      if (!isDragging && Math.abs(dx) < 5) return;
      if (!isDragging) {
        isDragging = true;
        content.classList.add('assets-slider_content--dragging');
        // Capture only once dragging, otherwise the click is retargeted
        // from the clicked item to the container and never opens the asset
        content.setPointerCapture(e.pointerId);
      }
      content.scrollLeft = startScrollLeft - dx;
    };

    const stopDragging = (e: PointerEvent) => {
      if (!isPointerDown) return;
      isPointerDown = false;
      content.classList.remove('assets-slider_content--dragging');
      try {
        content.releasePointerCapture(e.pointerId);
      } catch {
        // already released
      }
      if (!isDragging) return;
      isDragging = false;
      const suppressClick = (clickEvent: MouseEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        content.removeEventListener('click', suppressClick, true);
      };
      content.addEventListener('click', suppressClick, true);
    };

    content.addEventListener('pointerdown', onPointerDown);
    content.addEventListener('pointermove', onPointerMove);
    content.addEventListener('pointerup', stopDragging);
    content.addEventListener('pointercancel', stopDragging);

    return () => {
      content.removeEventListener('pointerdown', onPointerDown);
      content.removeEventListener('pointermove', onPointerMove);
      content.removeEventListener('pointerup', stopDragging);
      content.removeEventListener('pointercancel', stopDragging);
      content.classList.remove('assets-slider_content--dragging');
    };
  }, [isAssetsSliderOpen, count]);

  if (count <= 1 || !isAssetsSliderOpen) {
    return null;
  }

  const sliderItems = renderSliderEntries(navigation);

  return (
    <div className="assets-slider assets-slider--desktop">
      <div
        className={classNames('assets-slider_desktop-panel', {
          'assets-slider_desktop-panel--no-review': !isApprovalList
        })}
      >
        {isApprovalList && (
          <div className="assets-slider_review-wrapper">
            <div className="assets-slider_review">
              <div
                className={classNames('assets-slider_review-icon', {
                  'assets-slider_review-icon--reviewed': isReviewed
                })}
              >
                <CheckTaskIcon />
              </div>
              <div className="assets-slider_review-label">
                Review{reviewPositionLabel ? `: ${reviewPositionLabel}` : ''}
              </div>
            </div>
          </div>
        )}
        <div ref={contentWrapRef} className="assets-slider_content-wrap">
          <div ref={contentRef} className="assets-slider_content">
            {isSelectedAssetPending ? (
              <AssetsSliderSkeleton count={count} />
            ) : (
              sliderItems
            )}
          </div>
        </div>
        <div
          className="assets-slider_toggle"
          role="button"
          tabIndex={0}
          onClick={() => dispatch(setAssetsSliderOpen(false))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              dispatch(setAssetsSliderOpen(false));
            }
          }}
        >
          <ArrowDownIcon className="assets-slider_toggle-chevron" />
        </div>
      </div>
    </div>
  );
}

export default function AssetsSlider() {
  const { isMdWidth } = useResponsive();

  if (isMdWidth) {
    return <AssetsSliderMobile />;
  }

  return <AssetsSliderDesktop />;
}
